import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Input, Button, Tooltip } from 'antd';

import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import useSwr from '@hooks/useSwr';

import useSearchParams from '@hooks/useSearchParams';
import { FileSearchOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useCheckRole } from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import SelectCustomers from '@components/SelectCustomers';
import CustomTag from '@components/CustomTag';
import { useNavigate } from 'react-router-dom';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { bytesFormat, dateFormat } from '@utils/formatters';
import NewSharedPlanModal, { SharedPlanPayload } from './NewSharedPlanModal';

const { Search } = Input;

interface SharedPlansData {
  content: SharedPlanPayload[];
  totalElements?: number;
}

const SharedPlans = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['groupings', 'attributes']);
  const canManage = useCheckRole({
    role: rolesConstants.MANAGE_SHARED_PLANS,
  });
  const viewAlerts = useCheckRole({ role: rolesConstants.VIEW_ALERTS });

  const [nameValue, setNameValue] = useState<string>();
  const [openModal, setOpenModal] = useState(false);

  const toggleOpen = useCallback(() => setOpenModal(crr => !crr), []);

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['showContracted'],
    extraParams: ['name', 'departmentId'],
  });

  const { name, departmentId, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const { data, mutate } = useSwr<SharedPlansData>(
    `/service-proxy/broker/sharing-plans`,
    {
      params: {
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
        name,
        departmentId,
      },
    },
  );

  useEffect(() => {
    setNameValue(name);
  }, [name]);

  const columns = useMemo(
    () => [
      {
        title: t('attributes:id'),
        dataIndex: 'id',
      },
      {
        title: t('attributes:description'),
        dataIndex: 'description',
      },
      {
        title: t('attributes:department'),
        dataIndex: 'department',
        render: (department: { name: string }) => department?.name,
      },
      {
        title: t('attributes:automaticBlocking.title'),
        dataIndex: 'automaticBlocking',
        align: 'center' as const,
        render: (value: string) => (
          <CustomTag color={value ? 'green' : 'red'}>
            {t(
              `attributes:automaticBlocking.${
                value ? 'activated' : 'disabled'
              }`,
            ).toUpperCase()}
          </CustomTag>
        ),
      },
      {
        title: t('attributes:franchise'),
        dataIndex: 'franchiseBytes',
        align: 'center' as const,
        render: bytesFormat,
      },
      ...(viewAlerts
        ? [
            {
              title: t('attributes:lastAlertDate'),
              dataIndex: 'lastAlert',
              align: 'center' as const,
              render: (value: string) => value && dateFormat(value),
            },
          ]
        : []),
      {
        title: '',
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: SharedPlanPayload) => (
          <Tooltip title={t('buttons.details')}>
            <Button
              icon={<FileSearchOutlined />}
              disabled={!record?.id}
              onClick={() => {
                navigate(
                  `/${ApplicationsConstants.MOT_BUSINESS}/mobile-network/administrative/shared-plans/${record.id}`,
                );
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [navigate, t, viewAlerts],
  );

  const handleMutate = useCallback(
    (newSharedPlan: SharedPlanPayload) => {
      if (data) {
        mutate({
          ...data,
          content: [newSharedPlan, ...data?.content].slice(0, 10),
        });
      }
    },
    [data, mutate],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('attributes:description'),
        element: (
          <Search
            onSearch={value => setSearchParams({ name: value })}
            onChange={({ target: { value } }) => setNameValue(value)}
            value={nameValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:department'),
        element: (
          <SelectCustomers
            value={departmentId && Number(departmentId)}
            onChange={value => setSearchParams({ departmentId: value })}
            isMotBusiness
          />
        ),
      },
    ],
    [t, setSearchParams, nameValue, departmentId],
  );

  return (
    <>
      {canManage && (
        <NewSharedPlanModal
          open={openModal}
          onClose={toggleOpen}
          mutateData={handleMutate}
        />
      )}

      <Table
        title={t('sharedPlans')}
        tableKey="sharedPlans"
        columns={columns}
        data={data?.content ?? []}
        loading={!data?.content}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        extraActions={
          <>
            {canManage && (
              <Tooltip title={t('newSharedPlan')}>
                <Button icon={<PlusCircleOutlined />} onClick={toggleOpen} />
              </Tooltip>
            )}
          </>
        }
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
      />
    </>
  );
};

export default SharedPlans;
